import type { EPortal } from '@/frontend/src/types/portal';
import { getGeoPortalIdentifier } from '@/frontend/src/helpers/portal';

import type {
    IAutocompleteApiResponse,
    IAutocompleteParams,
    IFeedbackApiResponse,
    IFeedbackParams,
    IGeocodingApiResponse,
    IGeocodingParams,
} from '../types/geo';

const autocompleteApiInstanceId = crypto.randomUUID();

const autocompleteApiHeaders = (portal: EPortal) => ({
    ['X-instance-id']: autocompleteApiInstanceId,
    ['X-place']: 'ESTIMATION_FUNNEL',
    ['X-channel']: 'WEB_BROWSER',
    ['X-producer-cap']: 'ESFU',
    ['X-portal']: getGeoPortalIdentifier(portal),
});

export const queryString = (
    params: IAutocompleteParams | IFeedbackParams | IGeocodingParams,
): string => {
    return Object.entries(params)
        .map(([key, value]) => {
            if (Array.isArray(value)) {
                return value.map((v) => `${key}=${encodeURIComponent(v)}`);
            }

            return `${key}=${encodeURIComponent(value)}`;
        })
        .flat()
        .join('&');
};

export const getApiResponse = async (
    params: IAutocompleteParams | IFeedbackParams | IGeocodingParams,
    baseUrl: string,
    portal?: EPortal,
): Promise<
    IAutocompleteApiResponse | IFeedbackApiResponse | IGeocodingApiResponse
> => {
    const queryParams = queryString(params);
    const url = `${baseUrl}?${queryParams}`;

    const response = await fetch(url, {
        method: 'GET',
        headers: {
            'X-api-key': process.env.GEO_API_KEY,
            ...(portal ? autocompleteApiHeaders(portal) : {}),
        },
    });

    if (!response.ok) {
        throw new Error(`Error: ${response.status} ${response.statusText}`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return response.json();
};

export const autocompleteApiRequest = async (
    params: IAutocompleteParams,
    portal: EPortal,
): Promise<IAutocompleteApiResponse> => {
    const baseUrl = `${process.env.GEO_AUTOCOMPLETE_API_URL}/autocomplete`;

    return (await getApiResponse(
        params,
        baseUrl,
        portal,
    )) as IAutocompleteApiResponse;
};

export const feedbackApiRequest = async (
    params: IFeedbackParams,
    portal: EPortal,
): Promise<IFeedbackApiResponse> => {
    const baseUrl = `${process.env.GEO_AUTOCOMPLETE_API_URL}/feedback`;

    return (await getApiResponse(
        params,
        baseUrl,
        portal,
    )) as IFeedbackApiResponse;
};

export const geocodingApiRequest = async (
    params: IGeocodingParams,
): Promise<IGeocodingApiResponse> => {
    const baseUrl = `${process.env.GEO_GEOCODING_API_URL}/geocode/multiline`;

    return (await getApiResponse(params, baseUrl)) as IGeocodingApiResponse;
};
